import React, { useMemo, useState } from "react";
import { Box, Container, Typography, Pagination } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";

import coach1 from "../../static/media/AboutUs/coach1.webp";
import coach2 from "../../static/media/AboutUs/coach2.webp";
import coach3 from "../../static/media/AboutUs/coach3.webp";
import coach4 from "../../static/media/AboutUs/coach4.webp";

const coaches = [
  {
    name: "MAYLING BONILLA",
    role: "Coach de Fitness",
    branch: "Centro de Libertad",
    image: coach1,
    description:
      "Mayling Bonilla es coach de fitness dedicada a guiar a las personas en su proceso de transformación física y mental. Su enfoque se basa en el entrenamiento, la disciplina y la creación de hábitos saludables, ayudando a sus alumnos a alcanzar resultados reales, sostenibles y alineados con sus objetivos personales.",
    title: "Enfoque de trabajo",
    items: [
      "Transformación física y mental",
      "Desarrollo de hábitos saludables",
      "Disciplina y constancia en el entrenamiento",
      "Resultados reales y sostenibles",
    ],
  },
  {
    name: "DANNA GUERRERO SANTOS",
    role: "Entrenadora de CrossFit",
    branch: "Santa Elena",
    image: coach2,
    description:
      "Danna Guerrero Santos es entrenadora de CrossFit y entrenamiento funcional, con 5 años de experiencia en esta disciplina. Su enfoque se centra en el desarrollo del rendimiento físico, la técnica y la constancia, acompañando a sus alumnos en procesos de mejora continua mediante una planificación adecuada y entrenamiento estructurado.",
    title: "Formación y experiencia",
    items: [
      "5 años de experiencia en CrossFit",
      "Entrenadora funcional",
      "CrossTraining Coach L1",
      "Programa de levantamiento olímpico de pesas Nivel 1",
      "Programación para CrossTraining",
    ],
  },
  {
    name: "KEVIN VERA MÉNDEZ",
    role: "Entrenador de CrossFit",
    branch: "Santa Elena",
    image: coach3,
    description:
      "Kevin Vera Méndez es entrenador de CrossFit y entrenamiento funcional, con 8 años de experiencia en esta disciplina. Su perfil combina la práctica constante, la formación académica en curso y la capacitación técnica en distintas áreas del entrenamiento, lo que le permite acompañar a sus alumnos en procesos de mejora física, técnica y rendimiento deportivo.",
    title: "Formación y experiencia",
    items: [
      "8 años de experiencia en CrossFit",
      "Cursando la carrera de Entrenamiento Deportivo",
      "Entrenador funcional",
      "Asistencia a Level 1 CrossFit",
      "Curso de entrenamiento para entrenadores",
      "Curso de levantamiento",
      "Curso de gimnasia",
    ],
  },
  {
    name: "ASHLEY ENDO",
    role: "Auxiliar en Rehabilitación Física y Coach en Entrenamiento Funcional",
    branch: "Paseo Shopping",
    image: coach4,
    description:
      "Ashley Endo es auxiliar en Rehabilitación Física y coach especializada en Entrenamiento Funcional. Se caracteriza por su enfoque dinámico, seguro y orientado al bienestar integral, ayudando a sus alumnos a mejorar su fuerza, resistencia y movilidad mediante rutinas adaptadas y acompañamiento constante. Su objetivo es motivar a cada persona a superar sus límites, desarrollar hábitos saludables y disfrutar del proceso de entrenamiento mientras avanza hacia su mejor versión.",
    title: "Enfoque de trabajo",
    items: [
      "Mejora de fuerza, resistencia y movilidad",
      "Entrenamiento funcional dinámico y seguro",
      "Desarrollo de hábitos saludables",
      "Motivación y acompañamiento en el proceso de cambio físico",
    ],
  },
];

const MotionBox = motion(Box);

const fadeUp = {
  hidden: { opacity: 0, y: 34 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: "easeOut" },
  },
};

const pageVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: "easeOut",
      staggerChildren: 0.08,
    },
  },
  exit: {
    opacity: 0,
    y: -18,
    transition: { duration: 0.25, ease: "easeInOut" },
  },
};

export default function CoachesSection() {
  const [page, setPage] = useState(1);
  const perPage = 2;
  const totalPages = Math.ceil(coaches.length / perPage);

  const visibleCoaches = useMemo(() => {
    const start = (page - 1) * perPage;
    return coaches.slice(start, start + perPage);
  }, [page]);

  const handleChangePage = (_, value) => {
    setPage(value);

    setTimeout(() => {
      const section = document.getElementById("coach-novaxfit");
      if (section) {
        section.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 80);
  };

  return (
    <Box
      id="coach-novaxfit"
      component="section"
      sx={{
        background: "#ffffff",
        py: { xs: 6, md: 10 },
        overflow: "hidden",
        scrollMarginTop: { xs: "82px", md: "96px" },
      }}
    >
      <Container maxWidth="lg">
        <MotionBox
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          sx={{
            maxWidth: 850,
            mx: "auto",
            textAlign: "center",
            mb: { xs: 4.5, md: 7 },
          }}
        >
          <Typography
            sx={{
              color: "#e50914",
              fontSize: { xs: 12, md: 13 },
              fontWeight: 900,
              letterSpacing: "0.26em",
              textTransform: "uppercase",
              mb: 1.5,
            }}
          >
            Coaches NovaXFit
          </Typography>

          <Typography
            component="h2"
            sx={{
              color: "#111113",
              fontWeight: 800,
              lineHeight: 0.96,
              letterSpacing: "-0.06em",
              fontSize: {
                xs: "clamp(34px,5vw,48px)",
                md: "clamp(52px,3vw,76px)",
              },
            }}
          >
            Equipo que entrena contigo
          </Typography>

          <Typography
            sx={{
              mt: 2.4,
              color: "rgba(17,17,19,0.68)",
              fontSize: { xs: 15, md: 17 },
              lineHeight: 1.75,
              fontWeight: 500,
            }}
          >
            En NovaXFit contamos con un equipo de coaches comprometidos con el
            desarrollo físico, mental y deportivo de cada alumno. Cada
            entrenador aporta experiencia, preparación y un enfoque único para
            acompañarte en tu proceso, ayudarte a alcanzar tus objetivos y
            potenciar tu mejor versión.
          </Typography>
        </MotionBox>

        <AnimatePresence mode="wait">
          <MotionBox
            key={page}
            variants={pageVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: { xs: 3, md: 4 },
            }}
          >
            {visibleCoaches.map((coach) => (
              <MotionBox
                key={coach.name}
                variants={fadeUp}
                sx={{
                  background: "#ffffff",
                  border: "1px solid rgba(17,17,19,0.12)",
                  display: "grid",
                  gridTemplateRows: "auto 1fr",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    aspectRatio: { xs: "16 / 10", sm: "16 / 9" },
                    background: "#0d0d0f",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    component="img"
                    src={coach.image}
                    alt={`${coach.name} ${coach.role}`}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "center",
                      display: "block",
                    }}
                  />

                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(180deg, rgba(0,0,0,0.02) 15%, rgba(0,0,0,0.70) 100%)",
                    }}
                  />

                  <Box
                    sx={{
                      position: "absolute",
                      left: { xs: 18, md: 24 },
                      right: { xs: 18, md: 24 },
                      bottom: { xs: 18, md: 22 },
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#ffffff",
                        fontSize: {
                          xs: "clamp(27px,8vw,38px)",
                          md: "clamp(30px,3.6vw,46px)",
                        },
                        fontWeight: 900,
                        lineHeight: 0.95,
                        letterSpacing: "-0.055em",
                        textShadow: "0 12px 35px rgba(0,0,0,0.75)",
                      }}
                    >
                      {coach.name}
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.8,
                        color: "#ffffff",
                        fontSize: { xs: 14, md: 15 },
                        fontWeight: 800,
                        lineHeight: 1.25,
                        textShadow: "0 8px 24px rgba(0,0,0,0.75)",
                      }}
                    >
                      {coach.role}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    p: { xs: 2.4, md: 3 },
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#e50914",
                      fontSize: 12.5,
                      fontWeight: 900,
                      letterSpacing: "0.13em",
                      textTransform: "uppercase",
                      mb: 1.8,
                    }}
                  >
                    Sucursal: {coach.branch}
                  </Typography>

                  <Typography
                    sx={{
                      color: "rgba(17,17,19,0.74)",
                      fontSize: { xs: 14.5, md: 15.2 },
                      lineHeight: 1.68,
                      fontWeight: 500,
                      mb: 2.5,
                    }}
                  >
                    {coach.description}
                  </Typography>

                  <Box
                    sx={{
                      mt: "auto",
                      pt: 2.4,
                      borderTop: "1px solid rgba(17,17,19,0.10)",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#111113",
                        fontSize: 13,
                        fontWeight: 900,
                        letterSpacing: "0.14em",
                        textTransform: "uppercase",
                        mb: 1.5,
                      }}
                    >
                      {coach.title}
                    </Typography>

                    <Box
                      component="ul"
                      sx={{
                        m: 0,
                        p: 0,
                        listStyle: "none",
                        display: "grid",
                        gap: 1,
                      }}
                    >
                      {coach.items.map((item) => (
                        <Box
                          component="li"
                          key={item}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "18px 1fr",
                            alignItems: "start",
                            color: "rgba(17,17,19,0.78)",
                            fontSize: { xs: 14, md: 14.5 },
                            lineHeight: 1.5,
                            fontWeight: 650,
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              width: 8,
                              height: 8,
                              background: "#e50914",
                              mt: "7px",
                            }}
                          />
                          <Box component="span">{item}</Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </MotionBox>
            ))}
          </MotionBox>
        </AnimatePresence>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: { xs: 4, md: 5 },
          }}
        >
          <Pagination
            count={totalPages}
            page={page}
            onChange={handleChangePage}
            shape="rounded"
            size="large"
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: 0,
                fontWeight: 900,
                color: "#111113",
                border: "1px solid rgba(17,17,19,0.16)",
              },
              "& .MuiPaginationItem-root.Mui-selected": {
                background: "#e50914",
                color: "#ffffff",
                borderColor: "#e50914",
              },
              "& .MuiPaginationItem-root.Mui-selected:hover": {
                background: "#e50914",
              },
            }}
          />
        </Box>
      </Container>
    </Box>
  );
}